import { Button } from '@artstorefronts/ui';
import { WandSparklesIcon } from 'lucide-react';
import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../context/analytics';

interface SuggestionInputProps {
  suggestion: string;
  setSuggestion: (suggestion: string) => void;
  loading: boolean;
  onSubmitSuggestion: () => void;
  isDisabled: boolean;
  showShadow?: boolean;
}

const MIN_HEIGHT = 40;
const MAX_HEIGHT = 300;
const MOBILE_BREAKPOINT = 320;

function SuggestionInput({
  suggestion,
  setSuggestion,
  loading,
  onSubmitSuggestion,
  isDisabled,
  showShadow = true,
}: SuggestionInputProps) {
  const { t } = useTranslation('social');
  const { amplitude } = useAnalytics();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  const handleResize = useCallback(() => {
    if (!containerRef.current) return;
    setIsMobile(containerRef.current.offsetWidth < MOBILE_BREAKPOINT);
  }, []);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    const container = containerRef.current;
    if (!textarea || !container) return;

    textarea.style.height = `${MIN_HEIGHT}px`;
    const newHeight = suggestion ? Math.max(MIN_HEIGHT, Math.min(textarea.scrollHeight, MAX_HEIGHT)) : MIN_HEIGHT;

    textarea.style.height = `${newHeight}px`;
    container.style.height = `${newHeight}px`;
    container.style.bottom = '0px';
  }, [suggestion]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmitSuggestion();
    }
  };

  const handleSubmitSuggestionButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    if (suggestion) {
      amplitude.track('change_request', { change_prompt: suggestion });
      onSubmitSuggestion();
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleResize);
    const currentContainer = containerRef.current;

    if (currentContainer) {
      resizeObserver.observe(currentContainer);
      handleResize();
    }

    return () => resizeObserver.disconnect();
  }, [handleResize]);

  useEffect(() => {
    adjustHeight();
  }, [adjustHeight]);

  return (
    <div className="relative flex-1" style={{ height: `${MIN_HEIGHT}px` }}>
      <div ref={containerRef} className="absolute bottom-0 left-0 right-0 pt-1">
        <textarea
          ref={textareaRef}
          placeholder={isMobile ? t('suggestion.suggest-changes') : t('suggestion.suggest-changes-with-context')}
          className="w-full max-h-[300px] pr-12 py-2 px-3 resize-none overflow-y-auto border border-input bg-background ring-offset-none placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-md text-sm bottom-0"
          value={suggestion}
          disabled={loading || isDisabled}
          onChange={(e) => setSuggestion(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{ height: `${MIN_HEIGHT}px` }}
        />
        <Button
          type="submit"
          disabled={loading || isDisabled}
          onClick={handleSubmitSuggestionButtonClick}
          className="absolute bottom-[-4px] right-0 size-10 p-0"
        >
          <WandSparklesIcon className="size-4 pointer-events-none" />
        </Button>
        {showShadow && (
          <div
            className={`absolute top-[-40px] left-0 right-0 h-[40px] pointer-events-none`}
            style={{
              background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
            }}
          />
        )}
      </div>
    </div>
  );
}

export default SuggestionInput;
